import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import { Button, TextField } from '@mui/material';
import { AddPhotoAlternate, DeleteOutline } from '@mui/icons-material';
import { getAbsoluteFileUrl } from 'src/app/helpers/utils';
import { useEffect, useState } from 'react';
import useMessage from 'src/app/hooks/useMessage';
import { Controller, useForm } from 'react-hook-form';
import handleError from '@fuse/core/errorHandler';
import { createPortal } from 'react-dom';
import {
  useCreateEmojiMutation,
  useGetEmojiByIdQuery,
  useUpdateEmojiMutation,
} from 'src/app/services/emojiApi';
import SimpleDialog from '@fuse/core/SimpleDialog';

const ALLOWED_PATTERN = /^[\p{L}\p{N}]/u;

const defaultValues = {
  NameUz: '',
  NameRu: '',
};

const EmojisForm = ({ emoji, isOpen, onClose, setCurrentEmoji }) => {
  const { t } = useTranslation('translations');
  const { showMessage } = useMessage();
  const [file, setFile] = useState(null); 
  const [preview, setPreview] = useState(null);
  const [addEmoji, { isLoading: isCreateLoading }] = useCreateEmojiMutation();
  const [updateEmoji, { isLoading: isUpdateLoading }] = useUpdateEmojiMutation();
  const { data: emojiData } = useGetEmojiByIdQuery(
    { id: emoji?.id },
    {
      skip: !emoji?.id,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      keepUnusedDataFor: 0,
    }
  );

  const {
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isValid },
    reset,
    control,
  } = useForm({
    defaultValues,
    mode: 'onChange',
  });

  useEffect(() => {
    if (file) clearErrors('file');
  }, [file, clearErrors]);

  useEffect(() => {
    if (emoji?.id && emojiData) {
      reset({
        NameUz: emojiData.nameUz || '',
        NameRu: emojiData.nameRu || '',
      });
      setPreview(getAbsoluteFileUrl(emojiData?.filePath));
      setFile({ id: emojiData.id, src: emojiData?.filePath }); // mark as existing file
    } else if (!emoji?.id) {
      reset(defaultValues);
      setPreview(null);
      setFile(null);
    }
  }, [emojiData, emoji, reset]);

  const handleDialogClose = () => {
    onClose?.();
  };

  const trimObject = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
    );

  const makeTextRules = () => ({
    required: { value: true, message: t('RequiredField') },
    validate: (value) => {
      const v = (value ?? '').trim();
      if (!v) return t('RequiredField');
      if (!ALLOWED_PATTERN.test(v)) {
        return (
          t('SpecialCharactersNotAllowed') ||
          "Only letters, numbers, spaces and - . , _ ( ) / & : + ' are allowed"
        );
      }
      return true;
    },
  });

  const onSubmit = async (values) => {
    if (!file) {
      setError('file', { type: 'manual', message: t('ImageIsRequired') });
      return;
    }

    const { NameUz, NameRu } = trimObject(values);
    const isEdit = Boolean(emoji?.id);

    const formData = new FormData();
    formData.append('NameUz', NameUz);
    formData.append('NameRu', NameRu);

    if (!file?.id) {
      formData.append('file', file);
    }

    try {
      const res = isEdit
        ? await updateEmoji({ id: emoji.id, body: formData }).unwrap()
        : await addEmoji(formData).unwrap();

      if (res) {
        showMessage({ message: t('SuccessfullySaved'), variant: 'success' });
        handleDialogClose();
        setPreview(null);
        setFile(null);
        setCurrentEmoji?.(null);
        reset(defaultValues);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleFileChange = (event) => {
    const f = event.target.files?.[0];
    if (!f) {
      setPreview(null);
      setFile(null);
      return;
    }

    const isSvg = f.type === 'image/svg+xml' || /\.svg$/i.test(f.name);
    if (!isSvg) {
      setError('file', {
        type: 'manual',
        message: t('OnlySVGAllowed') || 'Only .svg files are allowed',
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
      setFile(f);
    };
    reader.readAsDataURL(f);
  };

  const handleDeleteFile = () => {
    setFile(null);
    setPreview(null);
  };

  const onChangeTrimStart = (fieldOnChange) => (e) => {
    fieldOnChange(e.target.value.trimStart());
  };

  return createPortal(
    <SimpleDialog open={isOpen} onClose={handleDialogClose}>
      <DialogTitle className="flex items-center justify-between">
        {emoji?.id ? <p>{t('Edit')}</p> : <p>{t('Create')}</p>}
      </DialogTitle>
      <DialogContent>
        <form className="flex flex-col mt-6 gap-14" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="NameUz"
            control={control}
            rules={makeTextRules()}
            render={({ field }) => (
              <TextField
                {...field}
                label={`${t('Title')} (uz.)`}
                size="small"
                error={Boolean(errors.NameUz)}
                helperText={errors.NameUz?.message}
                InputLabelProps={{ shrink: true }}
                onChange={onChangeTrimStart(field.onChange)}
              />
            )}
          />
          <Controller
            name="NameRu"
            control={control}
            rules={makeTextRules()}
            render={({ field }) => (
              <TextField
                {...field}
                label={`${t('Title')} (рус.)`}
                size="small"
                error={Boolean(errors.NameRu)}
                helperText={errors.NameRu?.message}
                InputLabelProps={{ shrink: true }}
                onChange={onChangeTrimStart(field.onChange)}
              />
            )}
          />
          {!preview && (
            <div className="flex flex-col items-center gap-2">
              <Button variant="text" color="success" component="label">
                <AddPhotoAlternate /> {t('AddLogo')}
                <input type="file" hidden accept=".svg,image/svg+xml" onChange={handleFileChange} />
              </Button>
              {errors?.file?.message && (
                <p className="text-sm text-red-600">{errors.file.message}</p>
              )}
            </div>
          )}
          {preview && (
            <div className="flex items-center justify-center gap-10">
              <img src={preview} alt="Preview" className="object-contain mt-2 w-96 h-96" />
              <DeleteOutline
                className="ml-10 cursor-pointer text-red text-20"
                onClick={handleDeleteFile}
              />
            </div>
          )}
          <Button
            disabled={!isValid || isCreateLoading || isUpdateLoading}
            type="submit"
            className="w-full"
            variant="contained"
            color="secondary"
          >
            <span className="mx-8">{t('Save')}</span>
          </Button>
        </form>
      </DialogContent>
    </SimpleDialog>,
    document.body
  );
};

export default EmojisForm;